/**
 * Membership API Functions
 *
 * API functions for company membership operations
 */

import type {
  CompanyMembership,
  UpdateMembershipDto,
  MembershipStatus,
} from '@lognai/plugin-admin';
import { apiFetch, buildQueryString, type PaginatedListResponse } from './client';
import type { ListQueryParams } from '@lognai/frontend-core';

const BASE_URL = '/api/companies';

/**
 * Membership with enriched data
 */
export interface EnrichedMembership extends CompanyMembership {
  user?: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  role?: {
    id: string;
    name: string;
    code: string;
  };
}

/**
 * Membership filter fields
 */
export interface MembershipFilter {
  search?: string;
  status?: MembershipStatus;
  roleId?: string;
  department?: string;
}

/**
 * Membership query params
 */
export type MembershipQueryParams = ListQueryParams<MembershipFilter>;

/**
 * Get list of members for a company
 */
export const listMembers = async (
  companyId: string,
  params?: MembershipQueryParams
): Promise<PaginatedListResponse<EnrichedMembership>> => {
  const query = buildQueryString(params as Record<string, unknown>);
  return apiFetch<PaginatedListResponse<EnrichedMembership>>(
    `${BASE_URL}/${encodeURIComponent(companyId)}/members${query}`
  );
};

/**
 * Get a single member by ID
 */
export const getMember = async (
  companyId: string,
  memberId: string
): Promise<EnrichedMembership> => {
  return apiFetch<EnrichedMembership>(
    `${BASE_URL}/${encodeURIComponent(companyId)}/members/${encodeURIComponent(memberId)}`
  );
};

/**
 * Update a member
 */
export const updateMember = async (
  companyId: string,
  memberId: string,
  dto: UpdateMembershipDto
): Promise<EnrichedMembership> => {
  return apiFetch<EnrichedMembership>(
    `${BASE_URL}/${encodeURIComponent(companyId)}/members/${encodeURIComponent(memberId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(dto),
    }
  );
};

/**
 * Remove a member
 */
export const removeMember = async (
  companyId: string,
  memberId: string,
  data?: { reason?: string; revokeAccess?: boolean }
): Promise<void> => {
  return apiFetch<void>(`${BASE_URL}/${encodeURIComponent(companyId)}/members/${encodeURIComponent(memberId)}`, {
    method: 'DELETE',
    body: data ? JSON.stringify(data) : undefined,
  });
};

/**
 * Remove a user from a company: that company's roles and access, its place in
 * their secondary companies or as their primary company (a company where they
 * hold an active full membership is promoted), and any membership rows. Signs the user out
 * everywhere. Their account and other companies are untouched.
 */
export const removeUserFromCompany = async (companyId: string, userId: string): Promise<void> => {
  return apiFetch<void>(
    `${BASE_URL}/${encodeURIComponent(companyId)}/users/${encodeURIComponent(userId)}`,
    { method: 'DELETE' }
  );
};
